import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { PlusIcon, PlusCircleIcon } from '@heroicons/react/16/solid'
import { Button, ButtonText } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import BottomSheet from '@/mobile/sheets/BottomSheet'
import type { DeviceClass } from '@/mobile/useDeviceClass'

const OPTIONS = [
  {
    type: 'in',
    label: 'Money In',
    sub: 'Income into a vault',
    glyph: '↓',
    tone: 'bg-green-lowest text-txt-success',
  },
  {
    type: 'out',
    label: 'Money Out',
    sub: 'Expense from a vault',
    glyph: '↑',
    tone: 'bg-purple-lowest text-txt-purple',
  },
  {
    type: 'transfer',
    label: 'Transfer',
    sub: 'Move money between vaults',
    glyph: '⇄',
    tone: 'bg-accent-tr-08 text-txt-accent',
  },
] as const

/** "+" in the top bar: an action sheet on phone, a dropdown on tablet. */
export default function AddTransactionMenu({
  device,
}: {
  device: Exclude<DeviceClass, 'desktop'>
}) {
  const navigate = useNavigate()
  const [open, setOpen] = React.useState(false)
  const pick = (type: string) => {
    setOpen(false)
    navigate(`/transactions/new?type=${type}`)
  }

  if (device === 'tablet') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className="rounded-lg">
            <PlusCircleIcon />
            <ButtonText>Add</ButtonText>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[230px] p-1">
          {OPTIONS.map((o) => (
            <DropdownMenuItem
              key={o.type}
              onSelect={() => pick(o.type)}
              className="flex items-center gap-2.5 px-2.5 py-2"
            >
              <span
                className={`flex size-6 items-center justify-center rounded-[7px] text-xs font-bold ${o.tone}`}
              >
                {o.glyph}
              </span>
              <span className="text-[13px] font-medium text-txt-primary">
                {o.label}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <>
      <Button
        iconOnly
        size="lg"
        aria-label="Add transaction"
        className="size-10 rounded-[9px]"
        onClick={() => setOpen(true)}
      >
        <PlusIcon className="size-5" />
      </Button>
      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        label="Add transaction"
        className="px-4"
      >
        <span className="px-1 pb-2 pt-3 text-xs font-semibold tracking-[.08em] text-txt-placeholder">
          ADD TRANSACTION
        </span>
        {OPTIONS.map((o) => (
          <button
            type="button"
            key={o.type}
            onClick={() => pick(o.type)}
            className="flex min-h-14 items-center gap-3 border-b border-light px-1 text-left last:border-b-0"
          >
            <span
              className={`flex size-8 items-center justify-center rounded-[9px] text-[15px] font-bold ${o.tone}`}
            >
              {o.glyph}
            </span>
            <span className="flex flex-col gap-px">
              <span className="text-[15px] font-semibold text-txt-primary">
                {o.label}
              </span>
              <span className="text-xs text-txt-secondary">{o.sub}</span>
            </span>
          </button>
        ))}
      </BottomSheet>
    </>
  )
}
